
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import AnimateInView from '@/components/ui/animate-in-view';
import { useToast } from "@/hooks/use-toast";
import axios from 'axios';
import HCaptcha from '@hcaptcha/react-hcaptcha';

// Import the schema and sub-components
import { contactFormSchema, ContactFormValues } from './ContactFormSchema';
import NameField from './FormFields/NameField';
import EmailField from './FormFields/EmailField';
import PhoneField from './FormFields/PhoneField';
import CompanyField from './FormFields/CompanyField';
import MessageField from './FormFields/MessageField';
import CaptchaComponent from './CaptchaComponent';
import FormStatusIndicator from './FormStatusIndicator';

const ContactForm = () => {
  const { toast } = useToast();
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const captchaRef = React.useRef<HCaptcha>(null);
  
  // Initialize react-hook-form with zod resolver
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      message: '',
      preferredContact: 'email',
      captchaToken: ''
    }
  });

  // Handle captcha token verification
  const onVerifyCaptcha = (token: string) => {
    setCaptchaToken(token);
    form.setValue('captchaToken', token);
  };

  const onSubmit = async (data: ContactFormValues) => {
    setFormState('submitting');
    
    try {
      // Replace with your actual API endpoint
      const response = await axios.post('http://localhost:3001/api/v1/contact/submit', {
        ...data,
        captchaToken: captchaToken
      });
      
      if (response.data.success) {
        setFormState('success');
        form.reset();
        toast({
          title: "Message Sent Successfully",
          description: "We'll get back to you as soon as possible.",
          variant: "default",
        });

        // Reset captcha
        captchaRef.current?.resetCaptcha();
        setCaptchaToken('');

        // Conversion tracking events
        try {
          // Placeholder for conversion tracking
          console.log('Placeholder: Conversion tracking would fire here');
        } catch (trackingError) {
          console.error('Error in conversion tracking:', trackingError);
          // Don't affect the user experience if tracking fails
        }
      } else {
        throw new Error(response.data.message || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setFormState('error');
      toast({
        title: "Error Sending Message",
        description: "There was a problem submitting your message. Please try again later.",
        variant: "destructive",
      });
      
      // Reset captcha on error
      captchaRef.current?.resetCaptcha();
      setCaptchaToken('');
    }
  };

  return (
    <AnimateInView animation="slide-up" className="glass-card p-8 shadow-luxury">
      <h2 id="contact-form" className="text-2xl mb-6 text-forti-fi">Send us a message</h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <NameField control={form.control} />
          <EmailField control={form.control} />
          <PhoneField control={form.control} />
          <CompanyField control={form.control} />
          <MessageField control={form.control} />
          
          {/* hCaptcha component */}
          <CaptchaComponent 
            onVerify={onVerifyCaptcha} 
            captchaRef={captchaRef} 
            error={form.formState.errors.captchaToken?.message} 
          />

          <Button 
            variant="gradient" 
            className="text-white w-full md:w-auto" 
            type="submit" 
            disabled={formState === 'submitting'}
          >
            {formState === 'submitting' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : 'Submit Message'}
          </Button>
          
          {/* Show success/error message */}
          {(formState === 'success' || formState === 'error') && 
            <FormStatusIndicator status={formState === 'success' ? 'success' : 'error'} />
          }
        </form>
      </Form>
    </AnimateInView>
  );
};

export default ContactForm;
